// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartPageTemplate, { CartPageTemplateStyles } from "@werner/App/Components/Cart/CartPageTemplate";
import AddressOverview from "@werner/App/Components/Common/AddressOverview";
import SectionTitle, { SectionTitleStyles } from "@werner/App/Components/Common/SectionTitle";
import XngZone from "@werner/App/Components/Common/XngZone";
import {
    checkoutReivewInfoStyles,
    checkoutReviewLabel,
    checkoutReviewLabelValueWrapper,
    checkoutReviewSectionTitle,
    checkoutReviewValue,
} from "@werner/App/SharedStyle/CheckoutPageStyles";
import orderDetailsSharedStyles from "@werner/App/SharedStyle/OrderDetailsSharedStyles";
import { DivWrapper, EMPTY_VALUE } from "@werner/XDL/constants";
import { color, getOneTimeAddress, mergeWithCss, parseBoolean, xScreen } from "@werner/XDL/Utilities";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

type Props = WidgetProps;

interface OrderDetailsOverviewStyles {
    container?: GridContainerProps;
    sectionGridItem?: GridItemProps;
    sectionGridContainer?: GridContainerProps;
    sectionColumn?: GridItemProps;
    sectionTitleStyles?: SectionTitleStyles;
    // XNG-Changes
    itemWrapper?: InjectableCss;
    itemText?: TypographyProps;
    itemValue?: TypographyProps;
    labelValueWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    shippingSectionColumn?: GridItemProps;
}

const orderDetailsOverviewStyles: OrderDetailsOverviewStyles = {
    container: { ...checkoutReivewInfoStyles.container },
    sectionGridItem: {
        ...checkoutReivewInfoStyles.sectionGridItem,
    },
    sectionGridContainer: { gap: 0 },
    sectionColumn: {
        ...checkoutReivewInfoStyles.sectionColumn,
    },
    sectionTitleStyles: {
        ...orderDetailsSharedStyles.sectionTitle,
    },
    itemWrapper: {
        ...mergeWithCss(checkoutReviewLabelValueWrapper, {
            css: css`
                margin-bottom: 0;
            `,
        }),
    },
    itemText: {
        ...checkoutReviewLabel,
    },
    itemValue: {
        ...checkoutReviewValue,
    },
    labelValueWrapper: { ...checkoutReviewLabelValueWrapper },
    label: {
        ...checkoutReviewLabel,
        css: css`
            ${checkoutReviewLabel?.css}
            white-space: nowrap;
        `,
    },
    value: { ...checkoutReviewValue },
    shippingSectionColumn: {
        ...checkoutReivewInfoStyles.sectionColumn,
        width: [12, 12, 12, 4, 4],
    },
};

const styles = orderDetailsOverviewStyles;
const OrderDetailsOverview: FC<Props> = ({ id }) => {
    const { value: order } = useContext(OrderStateContext);
    if (!order) {
        return null;
    }
    const oneTimeAddress = getOneTimeAddress(order);

    const isPickup = parseBoolean(order.properties?.isPickup);
    const isShareEntity = getIsShareEntity();

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.sectionGridItem}>
                <GridContainer {...styles.sectionGridContainer}>
                    <GridItem {...styles.sectionGridItem}>
                        <SectionTitle
                            title={translate("Order Overview")}
                            extendedStyles={styles.sectionTitleStyles}
                            className={isShareEntity ? "share-entity-section-title" : ""}
                        />
                    </GridItem>
                    <GridItem {...styles.sectionColumn}>
                        {createWidgetElement("OrderDetails/OrderDate", { fields: {} })}
                        {createWidgetElement("OrderDetails/OrderDetailsStatus", { fields: {} })}
                        {createWidgetElement("OrderDetails/OrderPo", { fields: {} })}
                        {createWidgetElement("OrderDetails/OrderDetailsShippingMethod", { fields: {} })}
                        {createWidgetElement("OrderDetails/OrderDetailsPaymentTerms", { fields: {} })}
                        {order.properties.customerReleaseNumber && (
                            <StyledWrapper {...styles.itemWrapper}>
                                <Typography {...styles.itemText} id="customerRel">
                                    {translate("Customer Rel#")}
                                </Typography>
                                <Typography
                                    {...styles.itemValue}
                                    aria-labelledby="customerRel"
                                    data-test-selector="customerRel"
                                >
                                    {order.properties.customerReleaseNumber}
                                </Typography>
                            </StyledWrapper>
                        )}
                        {order.properties.orderBy && (
                            <StyledWrapper {...styles.itemWrapper}>
                                <Typography {...styles.itemText} id="orderBy">
                                    {translate("Order By")}
                                </Typography>
                                <Typography
                                    {...styles.itemValue}
                                    aria-labelledby="orderBy"
                                    data-test-selector="orderBy"
                                >
                                    {order.properties.orderBy}
                                </Typography>
                            </StyledWrapper>
                        )}
                        <XngZone contentId={id} hint="Other Properties" zoneName="OtherProperties" />
                    </GridItem>
                    <GridItem {...styles.sectionColumn}>
                        {createWidgetElement("OrderDetails/OrderDetailsBillingAddress", { fields: {} })}
                        {createWidgetElement("OrderDetails/OrderDetailsShippingAddress", { fields: {} })}
                    </GridItem>
                    <GridItem {...styles.sectionColumn}>
                        <AddressOverview
                            title={translate("Delivery Information")}
                            companyName={oneTimeAddress.attention || oneTimeAddress.companyName}
                            address1={oneTimeAddress.address1}
                            address2={oneTimeAddress.address2}
                            city={oneTimeAddress.city}
                            state={oneTimeAddress.state ? oneTimeAddress.state.abbreviation : undefined}
                            postalCode={oneTimeAddress.postalCode}
                        />
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.sectionGridItem}>
                <GridContainer {...styles.sectionGridContainer}>
                    {order.notes && isPickup && (
                        <>
                            <GridItem {...styles.sectionGridItem}>
                                <SectionTitle
                                    title={translate("{0} Instructions", isPickup ? "Pickup" : "Shipping")}
                                    extendedStyles={styles.sectionTitleStyles}
                                />
                            </GridItem>
                            <GridItem {...styles.sectionGridItem}>
                                {createWidgetElement("OrderDetails/OrderDetailsNotes", { fields: {} })}
                            </GridItem>
                        </>
                    )}
                    {!isPickup && (
                        <>
                            <GridItem {...styles.sectionGridItem}>
                                <SectionTitle
                                    title={translate("Shipping")}
                                    extendedStyles={styles.sectionTitleStyles}
                                />
                            </GridItem>
                            <GridItem {...styles.shippingSectionColumn}>
                                <StyledWrapper {...styles.labelValueWrapper}>
                                    <Typography {...styles.label}>{translate("Shipping Instructions")}</Typography>
                                    <Typography {...styles.value}>{order.notes || EMPTY_VALUE}</Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.shippingSectionColumn}>
                                <StyledWrapper {...styles.labelValueWrapper}>
                                    <Typography {...styles.label}>{translate("Site Contact Phone Number")}</Typography>
                                    <Typography {...styles.value}>
                                        {order.properties.contactPhoneNumber || EMPTY_VALUE}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsOverview,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Order Details Overview",
    },
};

export default widgetModule;
